import { useDispatch } from "react-redux";

import { useQueryClient } from "react-query";

import {
  CardContent,
  CircularProgress,
  Link,
  Typography,
} from "@material-ui/core";
import _ from "lodash";
import React, { useMemo } from "react";

import { Alert } from "@material-ui/lab";
import { Link as RouterLink } from "react-router-dom";

import {
  envLocker,
  LocalStorageEnvironment,
} from "../../reducers/environments";
import useCurrentEnvironment from "../../hooks/use-environment";
import { userIsFPEmployee } from "../../util";
import { WebTrackerMonitoringSite } from "../types";
import { setCurrentEnvironment } from "../../actions/environments";

import useWTMPageStyles from "./useWTMPageStyles";
import useListWebTrackerMonitoringSites from "../queries/useListWebTrackerMonitoringSites";

type WTMOtherSiteSuggestion = WebTrackerMonitoringSite & {
  env: LocalStorageEnvironment;
};

export function EmptyState() {
  const classes = useWTMPageStyles();
  const dispatch = useDispatch();
  const shouldShowSetUpDomainPageLink = userIsFPEmployee();
  const queryClient = useQueryClient();

  const currentEnvironment = useCurrentEnvironment();

  const {
    isLoading: sitesLoading,
    isError: sitesError,
    data,
  } = useListWebTrackerMonitoringSites();

  /**
   * While this computation is trivial, ESLint recommends memoizing it because otherwise this value is consumed by
   * another hook, and could cause unnecessary re-renders if not memoized.
   */
  const webTrackerMonitoringSites = useMemo(() => {
    return data ?? [];
  }, [data]);

  const currentProjName = currentEnvironment?.project.name;
  const currentEnvName = currentEnvironment.name;

  const isWaitingForInitialReport = !!_.find(
    webTrackerMonitoringSites,
    (site) => site.envID === currentEnvironment.ID
  );

  const wtmEnvironmentsToSuggest: WTMOtherSiteSuggestion[] = useMemo(() => {
    if (webTrackerMonitoringSites === undefined) {
      return [];
    }

    const otherSites = webTrackerMonitoringSites.filter(
      (s) => s.envID !== currentEnvironment.ID
    );

    return _.uniqBy(otherSites, (site) => site.envID)
      .map((s) => {
        return {
          ...s,
          env: envLocker.getEnvByID(s.envID),
        };
      })
      .filter((s) => s.env !== null) as WTMOtherSiteSuggestion[];
  }, [webTrackerMonitoringSites, currentEnvironment.ID]);

  if (sitesLoading || !currentEnvironment.loaded) {
    return (
      <div className={classes.loader}>
        <CircularProgress />
      </div>
    );
  }

  if (sitesError || !currentEnvironment || !currentEnvironment.project) {
    return (
      <>
        "Something went wrong. Try refreshing the page, or contact
        support@freshpaint.io for assistance.";
      </>
    );
  }

  return (
    <CardContent className={classes.cardContent}>
      <div style={{ padding: "30px", textAlign: "center" }}>
        {isWaitingForInitialReport ? (
          <>
            <Typography component="h1" variant="h5">
              We&apos;re scanning {currentProjName} &gt; {currentEnvName} for
              Web Trackers. Check back tomorrow!
            </Typography>
            <div className={classes.loader}>
              <CircularProgress />
            </div>
          </>
        ) : (
          <>
            <Alert severity="info">
              <Typography component="h1" variant="h5">
                Web Tracker Monitoring is not set up for {currentProjName} &gt;{" "}
                {currentEnvName}.
              </Typography>
            </Alert>
            {shouldShowSetUpDomainPageLink && (
              <Typography variant="h6" style={{ marginTop: "1em" }}>
                <RouterLink to="/admin/trackers/setUpDomain">
                  <Link underline="always">
                    Click here to set up Web Tracker Monitoring
                  </Link>
                </RouterLink>{" "}
                for {currentProjName} &gt; {currentEnvName}
              </Typography>
            )}
          </>
        )}

        {wtmEnvironmentsToSuggest.map((site) => {
          return (
            <Typography key={site.envID} component="h1" variant="body1">
              <Link
                onClick={() => {
                  dispatch(setCurrentEnvironment(site.envID, queryClient));
                }}
                underline="always"
              >
                View Web Tracker Monitoring for {site.env.project.name} &gt;{" "}
                {site.env.name}
              </Link>
            </Typography>
          );
        })}
      </div>
    </CardContent>
  );
}
